import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import Navbar from '../components/Navbar';
import CardListing from '../components/CardListing';
import { LoadingSpinner, PageLoader } from '../components/LoadingSpinner';
import SlabRating, { SingleSlab } from '../components/SlabRating';
import { SellerBadges } from '../components/SellerBadges';
import { MembershipBadge } from '../components/MembershipBadge';
import { toast } from 'sonner';
import { Package, ShoppingBag, DollarSign, Star, Plus, Crown, ChevronRight, Pencil, Trash2, CheckCircle, Truck, AlertTriangle, Sparkles, ArrowUp } from 'lucide-react';

export const DashboardPage = () => {
  const { api, user, refreshUser, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const [loading, setLoading] = useState(true);
  const [listings, setListings] = useState([]);
  const [orders, setOrders] = useState([]);
  const [sales, setSales] = useState([]);
  const [sellerSummary, setSellerSummary] = useState(null);
  const [checkingPayment, setCheckingPayment] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, listing: null });
  const [deleting, setDeleting] = useState(false);
  const [upgrading, setUpgrading] = useState(false);
  const [upgradeDialog, setUpgradeDialog] = useState(false);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    
    fetchData();
    
    // Check for subscription success
    const sessionId = searchParams.get('session_id');
    const subscriptionSuccess = searchParams.get('subscription');
    if (sessionId && subscriptionSuccess === 'success') {
      handleSubscriptionSuccess(sessionId);
    }
  }, [isAuthenticated]);

  // Helper: check if user has seller access (Gold/Elite/Creator/Trial subscription)
  const hasSellerAccess = hasSellerAccess || 
    user?.membership_status === 'gold' || 
    user?.membership_status === 'elite' || 
    user?.membership_status === 'Creator' ||
    user?.is_seller;

  const fetchData = async () => {
    try {
      const requests = [
        api().get('/my-listings'),
        api().get('/orders')
      ];
      
      if (hasSellerAccess) {
        requests.push(api().get('/sales'));
        requests.push(api().get('/seller/dashboard-summary').catch(() => ({ data: null })));
      }
      
      const results = await Promise.all(requests);
      
      setListings(results[0].data);
      setOrders(results[1].data);
      
      if (hasSellerAccess) {
        setSales(results[2]?.data || []);
        setSellerSummary(results[3]?.data || null);
      }
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubscriptionSuccess = async (sessionId) => {
    setCheckingPayment(true);
    let attempts = 0;
    const maxAttempts = 5;
    
    const pollStatus = async () => {
      try {
        const response = await api().get(`/subscription/status/${sessionId}`);
        if (response.data.payment_status === 'paid') {
          await refreshUser();
          toast.success('Subscription activated! You can now list cards.');
          navigate('/dashboard', { replace: true });
          return;
        }
      } catch (error) {
        console.error('Status check error:', error);
      }
      
      attempts++;
      if (attempts < maxAttempts) {
        setTimeout(pollStatus, 2000);
      } else {
        toast.error('Could not verify payment. Please refresh the page.');
        setCheckingPayment(false);
      }
    };
    
    pollStatus();
  };

  const handleDeleteClick = (listing) => {
    setDeleteDialog({ open: true, listing });
  };

  // Upgrade to Elite handler
  const handleUpgradeToElite = async () => {
    setUpgrading(true);
    try {
      const response = await api().post('/subscription/upgrade-to-elite');
      if (response.data.success) {
        toast.success(response.data.message);
        await refreshUser();
        setUpgradeDialog(false);
      }
    } catch (error) {
      console.error('Upgrade error:', error);
      if (error.response?.status === 402) {
        toast.error(error.response.data.detail || 'Payment failed. Please update your card.');
      } else {
        toast.error(error.response?.data?.detail || 'Failed to upgrade. Please try again.');
      }
    } finally {
      setUpgrading(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteDialog.listing) return;
    
    setDeleting(true);
    try {
      await api().delete(`/cards/${deleteDialog.listing.id}`);
      toast.success('Listing deleted');
      setDeleteDialog({ open: false, listing: null });
      fetchData(); // Refresh listings
    } catch (error) {
      console.error('Delete error:', error);
      toast.error('Failed to delete listing');
    } finally {
      setDeleting(false);
    }
  };

  const handleMarkAsSold = async (listing) => {
    try {
      const response = await api().put(`/cards/${listing.id}/toggle-sold`);
      toast.success(response.data.message);
      fetchData(); // Refresh listings
    } catch (error) {
      console.error('Toggle sold error:', error);
      toast.error('Failed to update listing status');
    }
  };

  if (!isAuthenticated) return null;
  if (loading) return <PageLoader />;

  const totalEarnings = sales
    .filter(s => s.status === 'paid')
    .reduce((sum, s) => sum + s.item_price, 0);

  const activeListings = listings.filter(l => l.status === 'active');
  const soldListings = listings.filter(l => l.status === 'sold');

  // Helper to get proper image URL
  const getImageUrl = (imageSource) => {
    if (!imageSource) return null;
    if (imageSource.startsWith('/api')) {
      return `${process.env.REACT_APP_BACKEND_URL}${imageSource}`;
    }
    return imageSource;
  };

  // Custom listing card with actions
  const ListingCard = ({ card }) => {
    const imageUrl = getImageUrl(card.images?.[0] || card.image_url);
    
    return (
      <div className="stat-card p-4 relative group">
        {/* Status Badge */}
        {card.status === 'sold' && (
          <div className="absolute top-2 left-2 z-10 px-2 py-1 rounded-full bg-emerald-500/20 border border-emerald-500/30">
            <span className="text-xs font-medium text-emerald-400 flex items-center gap-1">
              <CheckCircle className="w-3 h-3" /> Sold
            </span>
          </div>
        )}
        
        {/* Card Image */}
        <Link to={`/cards/${card.id}`} className="block">
          <div className="aspect-[3/4] rounded-lg overflow-hidden bg-zinc-800 mb-3">
            {imageUrl ? (
              <img 
                src={imageUrl} 
                alt={card.player_name} 
                className={`w-full h-full object-cover ${card.status === 'sold' ? 'opacity-50' : ''}`}
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'flex';
                }}
              />
            ) : null}
            <div className={`w-full h-full items-center justify-center text-zinc-600 font-heading font-bold text-4xl ${imageUrl ? 'hidden' : 'flex'}`}>
              {card.player_name?.charAt(0)}
            </div>
          </div>
        </Link>
        
        {/* Card Info */}
      <Link to={`/cards/${card.id}`}>
        <h3 className="font-medium text-white truncate">{card.player_name}</h3>
        <p className="text-sm text-zinc-400 truncate">{card.year} {card.set_name}</p>
      </Link>
      
      {/* Price & Grade */}
      <div className="flex items-center justify-between mt-2">
        <span className="font-mono font-bold text-emerald-400">${card.price?.toFixed(2)}</span>
        {card.grade_company && (
          <span className="text-xs px-2 py-1 rounded bg-zinc-700 text-zinc-300">
            {card.grade_company} {card.grade_value || ''}
          </span>
        )}
      </div>
      
      {/* Action Buttons */}
      {card.status !== 'sold' && (
        <div className="flex gap-2 mt-3 pt-3 border-t border-zinc-700">
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate(`/edit-listing/${card.id}`)}
            className="flex-1 border-zinc-600 hover:border-blue-500"
            data-testid={`edit-listing-${card.id}`}
          >
            <Pencil className="w-3 h-3 mr-1" />
            Edit
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleMarkAsSold(card)}
            className="flex-1 border-zinc-600 hover:border-emerald-500 hover:text-emerald-400"
            data-testid={`mark-sold-${card.id}`}
          >
            <CheckCircle className="w-3 h-3 mr-1" />
            Mark Sold
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleDeleteClick(card)}
            className="border-zinc-600 hover:border-red-500 hover:text-red-400 px-2"
            data-testid={`delete-listing-${card.id}`}
          >
            <Trash2 className="w-3 h-3" />
          </Button>
        </div>
      )}
      
      {/* Sold - Relist option */}
      {card.status === 'sold' && (
        <div className="flex gap-2 mt-3 pt-3 border-t border-zinc-700">
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleMarkAsSold(card)}
            className="flex-1 border-zinc-600 hover:border-blue-500"
            data-testid={`relist-${card.id}`}
          >
            Relist Card
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleDeleteClick(card)}
            className="border-zinc-600 hover:border-red-500 hover:text-red-400 px-2"
          >
            <Trash2 className="w-3 h-3" />
          </Button>
        </div>
      )}
    </div>
    );
  };

  return (
    <div className="min-h-screen bg-[#09090B]">
      <div className="noise-overlay" />
      <Navbar />
      
      <main className="pt-24 pb-12 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div>
            <div className="flex items-center gap-3">
              <h1 className="font-heading font-bold text-4xl uppercase text-white">Dashboard</h1>
              <MembershipBadge membershipStatus={user?.membership_status} size="md" />
            </div>
            <p className="text-zinc-400 mt-1">Welcome back, {user?.name}</p>
          </div>
          
          {hasSellerAccess ? (
            <Link to="/sell">
              <Button className="btn-primary" data-testid="new-listing-btn">
                <Plus className="w-5 h-5 mr-2" />
                New Listing
              </Button>
            </Link>
          ) : (
            <Link to="/subscription">
              <Button className="btn-primary animate-pulse-glow" data-testid="subscribe-btn">
                <Crown className="w-5 h-5 mr-2" />
                Become a Seller
              </Button>
            </Link>
          )}
        </div>

        {checkingPayment && (
          <div className="mb-8 p-4 rounded-xl bg-blue-500/10 border border-blue-500/20 flex items-center gap-4">
            <LoadingSpinner size="md" />
            <p className="text-blue-400">Verifying your subscription payment...</p>
          </div>
        )}

        {/* Elite Coming Soon Card - For Gold Members Only */}
        {hasSellerAccess && user?.membership_status === 'gold' && (
          <div 
            className="mb-8 p-6 rounded-xl border border-zinc-700 bg-zinc-800/30"
            data-testid="elite-coming-soon-card"
          >
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div 
                  className="w-14 h-14 rounded-full flex items-center justify-center"
                  style={{ backgroundColor: 'rgba(0, 191, 255, 0.1)' }}
                >
                  <Sparkles className="w-7 h-7 text-cyan-400/50" />
                </div>
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="font-heading font-bold text-xl text-zinc-400">Elite Tier</h3>
                    <span className="px-2 py-0.5 rounded text-xs font-bold bg-zinc-700 text-zinc-400">COMING SOON</span>
                  </div>
                  <p className="text-zinc-500 text-sm">
                    Live Auctions feature is under development. Stay tuned!
                  </p>
                </div>
              </div>
              <div 
                className="flex items-center gap-2 px-6 py-2.5 rounded-lg bg-zinc-700/50 border border-zinc-600 whitespace-nowrap"
              >
                <Sparkles className="w-4 h-4 text-zinc-500" />
                <span className="text-zinc-400">Coming Soon</span>
              </div>
            </div>
          </div>
        )}

        {/* Upgrade Dialog */}
        <Dialog open={upgradeDialog} onOpenChange={setUpgradeDialog}>
          <DialogContent className="bg-zinc-900 border-zinc-800 max-w-md">
            <DialogHeader>
              <div className="flex items-center justify-center mb-4">
                <div 
                  className="w-16 h-16 rounded-full flex items-center justify-center"
                  style={{ backgroundColor: 'rgba(0, 191, 255, 0.2)' }}
                >
                  <Sparkles className="w-8 h-8" style={{ color: '#00BFFF' }} />
                </div>
              </div>
              <DialogTitle className="text-white text-center text-2xl">Upgrade to Elite</DialogTitle>
              <DialogDescription className="text-zinc-400 text-center">
                You'll be charged the prorated difference immediately. Your new Elite features will unlock instantly.
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 my-4">
              <div className="p-4 rounded-lg bg-white/5">
                <h4 className="font-medium text-white mb-3">You'll unlock:</h4>
                <ul className="space-y-2 text-sm">
                  <li className="flex items-center gap-2 text-cyan-300">
                    <CheckCircle className="w-4 h-4 text-emerald-400" />
                    Host Live Auctions
                  </li>
                  <li className="flex items-center gap-2 text-cyan-300">
                    <CheckCircle className="w-4 h-4 text-emerald-400" />
                    Combined Shipping ($5 + $0.50/item)
                  </li>
                  <li className="flex items-center gap-2 text-cyan-300">
                    <CheckCircle className="w-4 h-4 text-emerald-400" />
                    Shipping Subsidy Protection
                  </li>
                  <li className="flex items-center gap-2 text-cyan-300">
                    <CheckCircle className="w-4 h-4 text-emerald-400" />
                    Electric Blue Elite Badge
                  </li>
                </ul>
              </div>
              
              <div className="flex items-center justify-between p-3 rounded-lg bg-zinc-800">
                <span className="text-zinc-400">New Monthly Rate</span>
                <span className="font-mono font-bold text-xl" style={{ color: '#00BFFF' }}>$45/mo</span>
              </div>
            </div>
            
            <div className="flex gap-3">
              <Button 
                variant="outline" 
                className="flex-1 border-zinc-700"
                onClick={() => setUpgradeDialog(false)}
              >
                Maybe Later
              </Button>
              <Button 
                className="flex-1"
                style={{ backgroundColor: '#00BFFF', color: '#000' }}
                onClick={handleUpgradeToElite}
                disabled={upgrading}
                data-testid="confirm-upgrade-btn"
              >
                {upgrading ? <LoadingSpinner size="sm" /> : (
                  <>
                    <Sparkles className="w-4 h-4 mr-2" />
                    Upgrade Now
                  </>
                )}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
          <div className="stat-card">
            <Package className="w-8 h-8 text-blue-400 mb-4" />
            <p className="text-3xl font-mono font-bold text-white">{activeListings.length}</p>
            <p className="text-sm text-zinc-500">Active Listings</p>
          </div>
          
          <div className="stat-card">
            <ShoppingBag className="w-8 h-8 text-emerald-400 mb-4" />
            <p className="text-3xl font-mono font-bold text-white">{orders.length}</p>
            <p className="text-sm text-zinc-500">Orders</p>
          </div>
          
          <div className="stat-card">
            <DollarSign className="w-8 h-8 text-amber-400 mb-4" />
            <p className="text-3xl font-mono font-bold text-white">${totalEarnings.toFixed(2)}</p>
            <p className="text-sm text-zinc-500">Total Earnings</p>
          </div>
          
          <div className="stat-card">
            <div className="flex items-center gap-2 mb-4">
              <SingleSlab rating={user?.rating || 0} size="lg" />
              <SellerBadges 
                rating={user?.rating || 0} 
                totalSales={user?.total_sales || 0} 
                size="sm" 
              />
            </div>
            <p className={`text-3xl font-mono font-bold ${
              user?.rating >= 4.5 ? 'text-amber-400' : 
              user?.rating > 0 ? 'text-emerald-400' : 'text-zinc-400'
            }`}>{user?.rating?.toFixed(1) || '0.0'}</p>
            <p className="text-sm text-zinc-500">{user?.total_reviews || 0} Reviews</p>
          </div>
        </div>

        {/* Seller Payout Summary (if seller with sales) */}
        {hasSellerAccess && sellerSummary && sellerSummary.total_orders > 0 && (
          <div className="mb-12 stat-card p-6" data-testid="seller-payout-summary">
            <div className="flex items-center gap-3 mb-6">
              <Truck className="w-6 h-6 text-blue-400" />
              <h2 className="font-heading font-bold text-xl text-white">Seller Payout Summary</h2>
            </div>
            
            <div className="grid md:grid-cols-4 gap-6">
              {/* Net Earnings */}
              <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-lg p-4 text-center">
                <p className="text-zinc-400 text-sm mb-1">Net Earnings</p>
                <p className="text-2xl font-mono font-bold text-emerald-400">
                  ${sellerSummary.total_net_earnings?.toFixed(2)}
                </p>
              </div>
              
              {/* Pending Payouts */}
              <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-4 text-center">
                <p className="text-zinc-400 text-sm mb-1">Pending Payout</p>
                <p className="text-2xl font-mono font-bold text-amber-400">
                  ${sellerSummary.pending_payout?.amount?.toFixed(2)}
                </p>
                <p className="text-xs text-zinc-500">{sellerSummary.pending_payout?.count} orders</p>
              </div>
              
              {/* Shipping Subsidies */}
              <div className="bg-white/5 rounded-lg p-4 text-center">
                <p className="text-zinc-400 text-sm mb-1">Shipping Subsidies</p>
                <p className={`text-2xl font-mono font-bold ${sellerSummary.total_shipping_subsidies > 0 ? 'text-amber-400' : 'text-zinc-400'}`}>
                  ${sellerSummary.total_shipping_subsidies?.toFixed(2)}
                </p>
              </div>
              
              {/* Max Shipping Fee Setting */}
              <div className="bg-white/5 rounded-lg p-4 text-center">
                <p className="text-zinc-400 text-sm mb-1">Your Max Shipping Cap</p>
                <p className="text-2xl font-mono font-bold text-blue-400">
                  ${sellerSummary.max_shipping_fee?.toFixed(2)}
                </p>
                <Link to="/settings" className="text-xs text-blue-400 hover:underline">Edit</Link>
              </div>
            </div>
            
            {/* Shipping Subsidy Note */}
            {sellerSummary.total_shipping_subsidies > 0 && (
              <div className="mt-4 p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
                <p className="text-amber-200 text-sm">
                  {sellerSummary.subsidy_note}
                </p>
              </div>
            )}
          </div>
        )}

        {/* Subscription Status */}
        {!hasSellerAccess && (
          <div className="mb-12 stat-card p-8 text-center">
            <Crown className="w-12 h-12 text-amber-500 mx-auto mb-4" />
            <h2 className="font-heading font-bold text-2xl text-white mb-2">Unlock Selling</h2>
            <p className="text-zinc-400 mb-6">
              Subscribe for $20/month to list unlimited cards with 0% seller fees.
            </p>
            <Link to="/subscription">
              <Button className="btn-primary" data-testid="subscribe-cta">
                Subscribe Now
              </Button>
            </Link>
          </div>
        )}

        {/* My Listings */}
        <section className="mb-12">
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-heading font-bold text-2xl text-white">My Listings</h2>
            <div className="flex items-center gap-4">
              {hasSellerAccess && (
                <Link to="/seller-hub" className="text-amber-400 hover:text-amber-300 flex items-center gap-1 font-semibold">
                  Seller Hub <ChevronRight className="w-4 h-4" />
                </Link>
              )}
              {listings.length > 4 && (
                <Link to="/my-listings" className="text-blue-400 hover:text-blue-300 flex items-center gap-1">
                  View all <ChevronRight className="w-4 h-4" />
                </Link>
              )}
            </div>
          </div>
          
          {activeListings.length === 0 ? (
            <div className="stat-card p-8 text-center">
              <Package className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
              <p className="text-zinc-400">No active listings</p>
              {hasSellerAccess && (
                <Link to="/sell">
                  <Button variant="link" className="text-blue-400 mt-2">
                    Create your first listing
                  </Button>
                </Link>
              )}
            </div>
          ) : (
            <div className="card-grid">
              {activeListings.slice(0, 4).map((card) => (
                <ListingCard key={card.id} card={card} />
              ))}
            </div>
          )}
          
          {/* Sold Listings */}
          {soldListings.length > 0 && (
            <div className="mt-8">
              <h3 className="text-lg font-medium text-zinc-400 mb-4">Sold Cards ({soldListings.length})</h3>
              <div className="card-grid">
                {soldListings.slice(0, 2).map((card) => (
                  <ListingCard key={card.id} card={card} />
                ))}
              </div>
            </div>
          )}
        </section>

        {/* Recent Orders */}
        <section className="mb-12">
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-heading font-bold text-2xl text-white">Recent Orders</h2>
            {orders.length > 3 && (
              <Link to="/orders" className="text-blue-400 hover:text-blue-300 flex items-center gap-1">
                View all <ChevronRight className="w-4 h-4" />
              </Link>
            )}
          </div>
          
          {orders.length === 0 ? (
            <div className="stat-card p-8 text-center">
              <ShoppingBag className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
              <p className="text-zinc-400">No orders yet</p>
              <Link to="/marketplace">
                <Button variant="link" className="text-blue-400 mt-2">
                  Browse marketplace
                </Button>
              </Link>
            </div>
          ) : (
            <div className="space-y-4">
              {orders.slice(0, 3).map((order) => (
                <Link
                  key={order.id}
                  to={`/orders/${order.id}`}
                  className="stat-card p-4 flex items-center gap-4 hover:border-white/10 transition-colors"
                  data-testid={`order-${order.id}`}
                >
                  <div className="w-16 h-20 bg-zinc-800 rounded-lg overflow-hidden flex-shrink-0">
                    {order.card_details?.image_url ? (
                      <img src={order.card_details.image_url} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-zinc-600 font-heading font-bold">
                        {order.card_details?.player_name?.charAt(0)}
                      </div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-white truncate">{order.card_details?.player_name}</p>
                    <p className="text-sm text-zinc-400">{order.card_details?.year} {order.card_details?.set_name}</p>
                    <span className={`badge mt-2 ${order.status === 'paid' ? 'badge-emerald' : 'badge-amber'}`}>
                      {order.status}
                    </span>
                  </div>
                  <div className="text-right">
                    <p className="font-mono font-bold text-emerald-400">${order.total?.toFixed(2)}</p>
                    <ChevronRight className="w-5 h-5 text-zinc-500 ml-auto mt-2" />
                  </div>
                </Link>
              ))}
            </div>
          )}
        </section>

        {/* Sales (if seller) */}
        {hasSellerAccess && (
          <section>
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-heading font-bold text-2xl text-white">Recent Sales</h2>
              {sales.length > 3 && (
                <Link to="/sales" className="text-blue-400 hover:text-blue-300 flex items-center gap-1">
                  View all <ChevronRight className="w-4 h-4" />
                </Link>
              )}
            </div>
            
            {sales.length === 0 ? (
              <div className="stat-card p-8 text-center">
                <DollarSign className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
                <p className="text-zinc-400">No sales yet</p>
              </div>
            ) : (
              <div className="space-y-4">
                {sales.slice(0, 3).map((sale) => (
                  <Link
                    key={sale.id}
                    to={`/orders/${sale.id}`}
                    className="stat-card p-4 flex items-center gap-4 hover:border-white/10 transition-colors"
                    data-testid={`sale-${sale.id}`}
                  >
                    <div className="w-16 h-20 bg-zinc-800 rounded-lg overflow-hidden flex-shrink-0">
                      {sale.card_details?.image_url ? (
                        <img src={sale.card_details.image_url} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-zinc-600 font-heading font-bold">
                          {sale.card_details?.player_name?.charAt(0)}
                        </div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-white truncate">{sale.card_details?.player_name}</p>
                      <p className="text-sm text-zinc-400">{sale.card_details?.year} {sale.card_details?.set_name}</p>
                      <span className={`badge mt-2 ${sale.status === 'paid' ? 'badge-emerald' : 'badge-amber'}`}>
                        {sale.status}
                      </span>
                    </div>
                    <div className="text-right">
                      <p className="font-mono font-bold text-emerald-400">${sale.item_price?.toFixed(2)}</p>
                      <p className="text-xs text-zinc-500">You earned</p>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </section>
        )}
        
        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteDialog.open} onOpenChange={(open) => setDeleteDialog({ open, listing: null })}>
          <DialogContent className="bg-zinc-900 border-zinc-700">
            <DialogHeader>
              <DialogTitle className="text-white">Delete Listing</DialogTitle>
              <DialogDescription className="text-zinc-400">
                Are you sure you want to delete "{deleteDialog.listing?.player_name}"? This action cannot be undone.
              </DialogDescription>
            </DialogHeader>
            <div className="flex justify-end gap-3 mt-4">
              <Button 
                variant="outline" 
                onClick={() => setDeleteDialog({ open: false, listing: null })}
                className="border-zinc-600"
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={confirmDelete}
                disabled={deleting}
                className="bg-red-600 hover:bg-red-500"
                data-testid="confirm-delete-btn"
              >
                {deleting ? 'Deleting...' : 'Delete Listing'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
};

export default DashboardPage;
